'use client';

import * as React from 'react';

import { parseHtmlWithStyledLinks } from '@/utils/parseHtmlWithStyledLinks';

import { ContentText } from '@/components/molecules/ContentText';
import { ItemsContainer } from '@/components/layout/ItemsContainer';
import Section from '@/components/layout/Section';

import ListItem from './components/ListItem';
import type { ImagesItemsSectionConfigType } from './ImagesItemsSection.types';

export interface ImagesItemsSectionProps extends React.HTMLAttributes<HTMLDivElement> {
    config: ImagesItemsSectionConfigType;
}

const ImagesItemsSection = React.forwardRef<
    HTMLDivElement,
    ImagesItemsSectionProps
>(({ config, className, ...props }, ref) => (
    <Section
        id={config.id || props.id}
        className={className}
        ref={ref}
        {...props}
    >
        <ContentText
            title={typeof config.title === 'string' ? config.title : undefined}
            textConfig={Array.isArray(config.title) ? config.title : undefined}
            className="heading-spaces text-center"
            data-test-id={
                config['data-test-id'] && `${config['data-test-id']}-title`
            }
        />

        <ItemsContainer
            gridOptions={{ columns: config.columns }}
            swiperOptions={{
                swiperProps: {
                    className: 'pb-4 md:!hidden',
                    sliderClassName: 'max-w-full',
                    pagination: true,
                    slidesPerView: 1,
                    // Added slider breakpoint to looking alike production
                    breakpoints: {
                        560: {
                            slidesPerView: 2,
                            spaceBetween: 16,
                        },
                    },
                },
                breakpoint: 'md',
            }}
            className={config.className}
            data-test-id={
                config['data-test-id'] && `${config['data-test-id']}-list`
            }
        >
            {config.list.map(elem => (
                <ListItem
                    key={elem.id}
                    {...elem}
                    data-test-id={
                        config['data-test-id'] &&
                        `${config['data-test-id']}-list-item-${elem.id}`
                    }
                />
            ))}
        </ItemsContainer>

        {config.additionalText && (
            <div
                className="mt-8 text-2xs text-typo-secondary"
            >
                {parseHtmlWithStyledLinks(config.additionalText)}
            </div>
        )}
    </Section>
));

export default ImagesItemsSection;
